import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import _ from 'lodash';
import Ai from '../../store/models/Ai'
import Loader from '../Loader';
import { Button } from '../Button';
import { DailyChart, MultiSeriesChart } from '../Chart';
import { selectMobileLayout } from '../../store/UIState';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { Pagination } from '../Pagination';
import { getDatesBetween } from '../../utils';
import classNames from 'classnames';
import { AiTabs, AiTab } from '../AiTabs';


export const RagStatsTab = ({ chatbot, projectId }) => {
  const [selectedStatsTab, setSelectedStatsTab] = useState('chart');

  const onTabChanged = (tab) => {
    setSelectedStatsTab(tab);
  }

  return <div className='AiServicePage__tab-content'>
    <div className='RagChatbotDetailPage stats'>
      <AiTabs>
        <AiTab isSelected={(selectedStatsTab === 'chart')}
          onClick={() => onTabChanged('chart')}>
          Chart
        </AiTab>
        <AiTab isSelected={(selectedStatsTab === 'prompts')}
          onClick={() => onTabChanged('prompts')}>
          Prompts
        </AiTab>
      </AiTabs>

      {chatbot && chatbot.ui?.disable_tracking && <div className='RagChatbotDetailPage__analytics-disabled'>
        <div className='RagChatbotDetailPage__analytics-disabled--title'>Analytics are disabled for this chatbot</div>
        <div className='RagChatbotDetailPage__analytics-disabled--description'>Go to the settings tab to enable analytics</div>
      </div>}

      {selectedStatsTab === 'chart' && <ChartTab projectId={projectId} id={chatbot.id} />}
      {selectedStatsTab === 'prompts' && <PromptsTab projectId={projectId} id={chatbot.id} />}
    </div>
  </div>
}

const ChartTab = ({ projectId, id }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [chartData, setChartData] = useState({});
  const [sourceFilters, setSourceFilters] = useState({
    discord: true,
    web: true
  });
  const [typeFilters, setTypeFilters] = useState({
    dau: true,
    totalUsers: false,
    promptsCount: true,
    promptsPerUser: false,
    inputTokens: false,
    outputTokens: false
  });
  const [granularity, setGranularity] = useState('daily'); // 'daily', 'weekly', 'monthly'

  const currentDate = new Date();
  const iniStartDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, currentDate.getDate());
  const [startDate, setStartDate] = useState(iniStartDate);
  const [endDate, setEndDate] = useState(currentDate);
  const [isChanged, setIsChanged] = useState(false);

  // Memoize chart component to prevent unnecessary re-renders
  const memoizedChart = useMemo(() => {
    if (chartData.combinedChartData && chartData.series && chartData.series.length > 0) {
      return (
        <MultiSeriesChart 
          data={chartData.combinedChartData}
          series={chartData.series}
          granularity={granularity}
        />
      );
    }
    return (
      <div className='EmptyState'>
        <div className={'EmptyState__title'}>No metrics selected. Please enable at least one metric type.</div>
      </div>
    );
  }, [chartData.combinedChartData, chartData.series, granularity]);

  useEffect(() => {
    fetchChartData();
  }, [id])

  useEffect(() => {
    if (chartData.rawData) {
      processChartData();
    }
  }, [sourceFilters, granularity])
  
  // Disable totalUsers when switching to daily granularity
  useEffect(() => {
    if (granularity === 'daily' && typeFilters.totalUsers) {
      setTypeFilters(prev => ({
        ...prev,
        totalUsers: false
      }));
    }
  }, [granularity])
  
  // Separate effect for type filters - only updates series, doesn't recalculate data
  useEffect(() => {
    if (chartData.combinedChartData) {
      updateSeries();
    }
  }, [typeFilters])

  const handleSourceFilterChange = (source) => {
    setSourceFilters(prev => ({
      ...prev,
      [source]: !prev[source]
    }));
  };

  const handleTypeFilterChange = (type) => {
    setTypeFilters(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  // Color mapping for type filters - using colors from Usage page for better distinction
  const typeColors = {
    dau: '#18C99D',           // Green (same as usage page)
    totalUsers: '#4271fc',    // Blue
    promptsCount: '#e06ecd',  // Pink/Magenta
    promptsPerUser: '#fcee21', // Yellow
    inputTokens: '#FFA500',   // Orange
    outputTokens: '#663399'   // Purple
  };

  // Update series configuration without recalculating data
  const updateSeries = useCallback(() => {
    setChartData(prev => {
      if (!prev.combinedChartData) return prev;
      
      const series = [];
      const colors = typeColors;
      
      if (typeFilters.dau) {
        series.push({ key: 'dau', name: 'DAU', color: colors.dau });
      }
      if (typeFilters.totalUsers) {
        series.push({ key: 'totalUsers', name: 'Total Users', color: colors.totalUsers });
      }
      if (typeFilters.promptsCount) {
        series.push({ key: 'promptsCount', name: 'Prompts Count', color: colors.promptsCount });
      }
      if (typeFilters.promptsPerUser) {
        series.push({ key: 'promptsPerUser', name: 'Prompts/User', color: colors.promptsPerUser });
      }
      if (typeFilters.inputTokens) {
        series.push({ key: 'inputTokens', name: 'Input Tokens', color: colors.inputTokens });
      }
      if (typeFilters.outputTokens) {
        series.push({ key: 'outputTokens', name: 'Output Tokens', color: colors.outputTokens });
      }
      
      return {
        ...prev,
        series
      };
    });
  }, [typeFilters]);

  // Helper function to aggregate data by week or month
  const aggregateData = (dailyData, aggregationType) => {
    if (aggregationType === 'daily') return dailyData;
    
    const grouped = {};
    
    dailyData.forEach(item => {
      let key;
      const date = moment(item.date);
      
      if (aggregationType === 'weekly') {
        // Get the start of the week (Monday)
        key = date.startOf('week').format('YYYY-MM-DD');
      } else if (aggregationType === 'monthly') {
        // Get the first day of the month
        key = date.startOf('month').format('YYYY-MM-DD');
      }
      
      if (!grouped[key]) {
        grouped[key] = {
          date: key,
          result: 0,
          input_tokens: 0,
          output_tokens: 0,
          count: 0
        };
      }
      
      grouped[key].result += item.result || 0;
      grouped[key].input_tokens += item.input_tokens || 0;
      grouped[key].output_tokens += item.output_tokens || 0;
      grouped[key].count += 1;
    });
    
    return Object.values(grouped).sort((a, b) => moment(a.date).diff(moment(b.date)));
  };

  // Helper function to calculate average for DAU in aggregated periods
  const aggregateDAUData = (dailyData, aggregationType) => {
    if (aggregationType === 'daily') return dailyData;
    
    const grouped = {};
    
    dailyData.forEach(item => {
      let key;
      const date = moment(item.date);
      
      if (aggregationType === 'weekly') {
        key = date.startOf('week').format('YYYY-MM-DD');
      } else if (aggregationType === 'monthly') {
        key = date.startOf('month').format('YYYY-MM-DD');
      }
      
      if (!grouped[key]) {
        grouped[key] = {
          date: key,
          totalDAU: 0,
          totalUsers: 0, // Sum of daily unique users
          count: 0
        };
      }
      
      grouped[key].totalDAU += item.result;
      grouped[key].totalUsers += item.result; // Sum for total users per period
      grouped[key].count += 1;
    });
    
    return Object.values(grouped).map(group => ({
      date: group.date,
      result: Math.round(group.totalDAU / group.count), // Average DAU
      totalUsers: group.totalUsers // Total unique users in period
    })).sort((a, b) => moment(a.date).diff(moment(b.date)));
  };

  // Calculate summary statistics from daily data (independent of granularity)
  const calculateSummaryStats = (dailyDAUData, dailyPromptsData) => {
    if (!dailyDAUData || !dailyPromptsData || dailyDAUData.length === 0 || dailyPromptsData.length === 0) {
      return {
        avgDAU: 0,
        totalUsers: 0,
        peakDAU: { value: 0, date: null },
        totalPrompts: 0,
        avgPromptsPerUser: 0,
        comparison: null
      };
    }

    // Calculate average DAU across all days in the date range
    const totalDAU = dailyDAUData.reduce((sum, item) => sum + item.result, 0);
    const avgDAU = totalDAU / dailyDAUData.length;
    
    // Calculate total unique users (sum of daily unique users in the date range)
    const totalUsers = dailyDAUData.reduce((sum, item) => sum + item.result, 0);
    
    // Find peak DAU
    const peakDAU = dailyDAUData.reduce((peak, item) => {
      return item.result > peak.value ? { value: item.result, date: item.date } : peak;
    }, { value: 0, date: null });
    
    // Calculate total prompts across all days
    const totalPrompts = dailyPromptsData.reduce((sum, item) => sum + item.result, 0);
    const avgPromptsPerUser = totalDAU > 0 ? totalPrompts / totalDAU : 0;
    
    // No period-over-period comparison for now (could be added later if needed)
    const comparison = null;
    
    return {
      avgDAU: Math.round(avgDAU * 10) / 10,
      totalUsers: totalUsers,
      peakDAU,
      totalPrompts,
      avgPromptsPerUser: Math.round(avgPromptsPerUser * 10) / 10,
      comparison
    };
  };

  // Calculate period-over-period comparison
  const calculatePeriodComparison = (dauData, promptsData, granularity) => {
    if (!dauData || dauData.length < 2) return null;
    
    // For comparison, we'll compare the latest period with the previous period
    const sortedData = [...dauData].sort((a, b) => moment(a.date).diff(moment(b.date)));
    const sortedPrompts = [...promptsData].sort((a, b) => moment(a.date).diff(moment(b.date)));
    
    if (granularity === 'monthly' && sortedData.length >= 2) {
      const current = sortedData[sortedData.length - 1];
      const previous = sortedData[sortedData.length - 2];
      const currentPrompts = sortedPrompts[sortedPrompts.length - 1];
      const previousPrompts = sortedPrompts[sortedPrompts.length - 2];
      
      const dauChange = previous.result > 0 ? ((current.result - previous.result) / previous.result) * 100 : 0;
      const promptsChange = previousPrompts.result > 0 ? ((currentPrompts.result - previousPrompts.result) / previousPrompts.result) * 100 : 0;
      
      return {
        period: 'month',
        dauChange: Math.round(dauChange * 10) / 10,
        promptsChange: Math.round(promptsChange * 10) / 10
      };
    } else if (granularity === 'weekly' && sortedData.length >= 2) {
      const current = sortedData[sortedData.length - 1];
      const previous = sortedData[sortedData.length - 2];
      const currentPrompts = sortedPrompts[sortedPrompts.length - 1];
      const previousPrompts = sortedPrompts[sortedPrompts.length - 2];
      
      const dauChange = previous.result > 0 ? ((current.result - previous.result) / previous.result) * 100 : 0;
      const promptsChange = previousPrompts.result > 0 ? ((currentPrompts.result - previousPrompts.result) / previousPrompts.result) * 100 : 0;
      
      return {
        period: 'week',
        dauChange: Math.round(dauChange * 10) / 10,
        promptsChange: Math.round(promptsChange * 10) / 10
      };
    }
    
    return null;
  };

  const processChartData = () => {
    const { rawData } = chartData;
    if (!rawData) return;

    let sDate = moment(startDate).format('YYYY-MM-DD')
    let eDate = moment(endDate).format('YYYY-MM-DD')
    let dates = getDatesBetween(sDate, eDate);

    const data = {};

    // Check if both sources are enabled
    const bothSourcesEnabled = sourceFilters.discord && sourceFilters.web;

    if (bothSourcesEnabled) {
      // Use original data when both sources are enabled
      // Sum all entries for the same day in case there are duplicates
      data.clients_by_day = dates.map((date) => {
        const [year, month, day] = date.split("-").map(Number);
        const matches = (_.get(rawData, 'clients_by_day') || []).filter(
          (item) => item.year === year && item.month === month && item.day === day
        );
        const totalCount = matches.reduce((sum, item) => sum + (item.count || 0), 0);
        return {
          date,
          result: totalCount,
        };
      });

      data.prompts_count = dates.map((date) => {
        const [year, month, day] = date.split("-").map(Number);
        const matches = (_.get(rawData, 'prompts_count') || []).filter(
          (item) => item.year === year && item.month === month && item.day === day
        );
        const totalCount = matches.reduce((sum, item) => sum + (item.count || 0), 0);
        
        // Also get input/output tokens from prompts_by_source
        const promptsBySourceMatches = (_.get(rawData, 'prompts_by_source') || []).filter(
          (item) => item.year === year && item.month === month && item.day === day
        );
        const totalInputTokens = promptsBySourceMatches.reduce((sum, item) => sum + (item.input_tokens || 0), 0);
        const totalOutputTokens = promptsBySourceMatches.reduce((sum, item) => sum + (item.output_tokens || 0), 0);
        
        return {
          date,
          result: totalCount,
          input_tokens: totalInputTokens,
          output_tokens: totalOutputTokens,
        };
      });
    } else {
      // Use by_source calculation when filtering
      // Sum all entries for the same day and source in case there are duplicates
      data.clients_by_day = dates.map((date) => {
        const [year, month, day] = date.split("-").map(Number);

        let totalCount = 0;

        if (sourceFilters.discord) {
          const discordMatches = (_.get(rawData, 'clients_by_source') || []).filter(
            (item) => item.year === year && item.month === month && item.day === day && item.source === 'discord'
          );
          totalCount += discordMatches.reduce((sum, item) => sum + (item.count || 0), 0);
        }

        if (sourceFilters.web) {
          const webMatches = (_.get(rawData, 'clients_by_source') || []).filter(
            (item) => item.year === year && item.month === month && item.day === day && (item.source === null || item.source === undefined || item.source === 'web')
          );
          totalCount += webMatches.reduce((sum, item) => sum + (item.count || 0), 0);
        }

        return {
          date,
          result: totalCount,
        };
      });

      data.prompts_count = dates.map((date) => {
        const [year, month, day] = date.split("-").map(Number);

        let totalCount = 0;
        let totalInputTokens = 0;
        let totalOutputTokens = 0;

        if (sourceFilters.discord) {
          const discordMatches = (_.get(rawData, 'prompts_by_source') || []).filter(
            (item) => item.year === year && item.month === month && item.day === day && item.source === 'discord'
          );
          totalCount += discordMatches.reduce((sum, item) => sum + (item.count || 0), 0);
          totalInputTokens += discordMatches.reduce((sum, item) => sum + (item.input_tokens || 0), 0);
          totalOutputTokens += discordMatches.reduce((sum, item) => sum + (item.output_tokens || 0), 0);
        }

        if (sourceFilters.web) {
          const webMatches = (_.get(rawData, 'prompts_by_source') || []).filter(
            (item) => item.year === year && item.month === month && item.day === day && (item.source === null || item.source === undefined || item.source === 'web')
          );
          totalCount += webMatches.reduce((sum, item) => sum + (item.count || 0), 0);
          totalInputTokens += webMatches.reduce((sum, item) => sum + (item.input_tokens || 0), 0);
          totalOutputTokens += webMatches.reduce((sum, item) => sum + (item.output_tokens || 0), 0);
        }

        return {
          date,
          result: totalCount,
          input_tokens: totalInputTokens,
          output_tokens: totalOutputTokens,
        };
      });
    }

    // Apply aggregation based on granularity (for charts only)
    const aggregatedClientsData = aggregateDAUData(data.clients_by_day, granularity);
    const aggregatedPromptsData = aggregateData(data.prompts_count, granularity);
    
    // Separate data structures for different charts
    // DAU chart (average DAU only)
    const dauChartData = aggregatedClientsData.map(item => ({
      date: item.date,
      result: item.result
    }));
    
    // Total Users chart (for weekly/monthly only)
    const totalUsersChartData = granularity !== 'daily' 
      ? aggregatedClientsData.map(item => ({
          date: item.date,
          result: item.totalUsers || 0
        }))
      : [];
    
    // Prompts Count chart
    const promptsCountChartData = aggregatedPromptsData.map(item => ({
      date: item.date,
      result: item.result
    }));
    
    // Prompts per User chart
    const promptsPerUserChartData = aggregatedPromptsData.map((promptItem) => {
      const correspondingDAU = aggregatedClientsData.find(dau => dau.date === promptItem.date);
      // For weekly/monthly: use total users (sum of daily users), not average DAU
      // For daily: use result (DAU) directly
      const userCount = granularity !== 'daily' && correspondingDAU?.totalUsers 
        ? correspondingDAU.totalUsers 
        : correspondingDAU?.result || 0;
      const promptsPerUser = userCount > 0 
        ? promptItem.result / userCount 
        : 0;
      return {
        date: promptItem.date,
        result: Math.round(promptsPerUser * 10) / 10
      };
    });
    
    // Input Tokens chart
    const inputTokensChartData = aggregatedPromptsData.map(item => ({
      date: item.date,
      result: item.input_tokens || 0
    }));
    
    // Output Tokens chart
    const outputTokensChartData = aggregatedPromptsData.map(item => ({
      date: item.date,
      result: item.output_tokens || 0
    }));
    
    // Calculate summary statistics from raw daily data (independent of granularity)
    const summaryStats = calculateSummaryStats(data.clients_by_day, data.prompts_count);
    
    // Combine all metrics into a single data structure (always include all, filtering happens via series)
    const combinedChartData = aggregatedClientsData.map((dauItem) => {
      const promptItem = aggregatedPromptsData.find(p => p.date === dauItem.date);
      const userCount = granularity !== 'daily' && dauItem.totalUsers 
        ? dauItem.totalUsers 
        : dauItem.result || 0;
      const promptsPerUser = userCount > 0 && promptItem 
        ? promptItem.result / userCount 
        : 0;
      
      const combined = {
        date: dauItem.date,
        dau: dauItem.result,
        totalUsers: granularity !== 'daily' ? dauItem.totalUsers : dauItem.result,
        promptsCount: promptItem?.result || 0,
        promptsPerUser: Math.round(promptsPerUser * 10) / 10,
        inputTokens: promptItem?.input_tokens || 0,
        outputTokens: promptItem?.output_tokens || 0,
      };
      
      return combined;
    });
    
    // Build series configuration for MultiSeriesChart based on current typeFilters
    const series = [];
    const colors = typeColors;
    
    if (typeFilters.dau) {
      series.push({ key: 'dau', name: 'DAU', color: colors.dau });
    }
    if (typeFilters.totalUsers) {
      series.push({ key: 'totalUsers', name: 'Total Users', color: colors.totalUsers });
    }
    if (typeFilters.promptsCount) {
      series.push({ key: 'promptsCount', name: 'Prompts Count', color: colors.promptsCount });
    }
    if (typeFilters.promptsPerUser) {
      series.push({ key: 'promptsPerUser', name: 'Prompts/user', color: colors.promptsPerUser });
    }
    if (typeFilters.inputTokens) {
      series.push({ key: 'inputTokens', name: 'Input Tokens', color: colors.inputTokens });
    }
    if (typeFilters.outputTokens) {
      series.push({ key: 'outputTokens', name: 'Output Tokens', color: colors.outputTokens });
    }
    
    setChartData({ 
      ...chartData, 
      ...data,
      aggregated_clients_by_day: aggregatedClientsData,
      aggregated_prompts_count: aggregatedPromptsData,
      dauChartData,
      totalUsersChartData,
      promptsCountChartData,
      promptsPerUserChartData,
      inputTokensChartData,
      outputTokensChartData,
      summaryStats,
      combinedChartData,
      series
    });
  };

  const fetchChartData = async () => {
    try {
      setLoading(true);
      let res = await dispatch(Ai.actions.fetchChatbotChartData(id, projectId));
      let sDate = moment(startDate).format('YYYY-MM-DD')
      let eDate = moment(endDate).format('YYYY-MM-DD')
      let dates = getDatesBetween(sDate, eDate);

      let data;
      if (res) {
        data = { rawData: res };

        // Check if both sources are enabled
        const bothSourcesEnabled = sourceFilters.discord && sourceFilters.web;

        if (bothSourcesEnabled) {
          // Use original data when both sources are enabled
          // Sum all entries for the same day in case there are duplicates
          data.clients_by_day = dates.map((date) => {
            const [year, month, day] = date.split("-").map(Number);
            const matches = (_.get(res, 'clients_by_day') || []).filter(
              (item) => item.year === year && item.month === month && item.day === day
            );
            const totalCount = matches.reduce((sum, item) => sum + (item.count || 0), 0);
            return {
              date,
              result: totalCount,
            };
          });

          data.prompts_count = dates.map((date) => {
            const [year, month, day] = date.split("-").map(Number);
            const matches = (_.get(res, 'prompts_count') || []).filter(
              (item) => item.year === year && item.month === month && item.day === day
            );
            const totalCount = matches.reduce((sum, item) => sum + (item.count || 0), 0);
            
            // Also get input/output tokens from prompts_by_source
            const promptsBySourceMatches = (_.get(res, 'prompts_by_source') || []).filter(
              (item) => item.year === year && item.month === month && item.day === day
            );
            const totalInputTokens = promptsBySourceMatches.reduce((sum, item) => sum + (item.input_tokens || 0), 0);
            const totalOutputTokens = promptsBySourceMatches.reduce((sum, item) => sum + (item.output_tokens || 0), 0);
            
            return {
              date,
              result: totalCount,
              input_tokens: totalInputTokens,
              output_tokens: totalOutputTokens,
            };
          });
        } else {
          // Use by_source calculation when filtering
          // Sum all entries for the same day and source in case there are duplicates
          data.clients_by_day = dates.map((date) => {
            const [year, month, day] = date.split("-").map(Number);

            let totalCount = 0;

            if (sourceFilters.discord) {
              const discordMatches = (_.get(res, 'clients_by_source') || []).filter(
                (item) => item.year === year && item.month === month && item.day === day && item.source === 'discord'
              );
              totalCount += discordMatches.reduce((sum, item) => sum + (item.count || 0), 0);
            }

            if (sourceFilters.web) {
              const webMatches = (_.get(res, 'clients_by_source') || []).filter(
                (item) => item.year === year && item.month === month && item.day === day && (item.source === null || item.source === undefined || item.source === 'web')
              );
              totalCount += webMatches.reduce((sum, item) => sum + (item.count || 0), 0);
            }

            return {
              date,
              result: totalCount,
            };
          });

          data.prompts_count = dates.map((date) => {
            const [year, month, day] = date.split("-").map(Number);

            let totalCount = 0;
            let totalInputTokens = 0;
            let totalOutputTokens = 0;

            if (sourceFilters.discord) {
              const discordMatches = (_.get(res, 'prompts_by_source') || []).filter(
                (item) => item.year === year && item.month === month && item.day === day && item.source === 'discord'
              );
              totalCount += discordMatches.reduce((sum, item) => sum + (item.count || 0), 0);
              totalInputTokens += discordMatches.reduce((sum, item) => sum + (item.input_tokens || 0), 0);
              totalOutputTokens += discordMatches.reduce((sum, item) => sum + (item.output_tokens || 0), 0);
            }

            if (sourceFilters.web) {
              const webMatches = (_.get(res, 'prompts_by_source') || []).filter(
                (item) => item.year === year && item.month === month && item.day === day && (item.source === null || item.source === undefined || item.source === 'web')
              );
              totalCount += webMatches.reduce((sum, item) => sum + (item.count || 0), 0);
              totalInputTokens += webMatches.reduce((sum, item) => sum + (item.input_tokens || 0), 0);
              totalOutputTokens += webMatches.reduce((sum, item) => sum + (item.output_tokens || 0), 0);
            }

            return {
              date,
              result: totalCount,
              input_tokens: totalInputTokens,
              output_tokens: totalOutputTokens,
            };
          });
        }
      }
      // Apply aggregation based on granularity (for charts only)
      const aggregatedClientsData = aggregateDAUData(data.clients_by_day, granularity);
      const aggregatedPromptsData = aggregateData(data.prompts_count, granularity);
      
      // Separate data structures for different charts
      // DAU chart (average DAU only)
      const dauChartData = aggregatedClientsData.map(item => ({
        date: item.date,
        result: item.result
      }));
      
      // Total Users chart (for weekly/monthly only)
      const totalUsersChartData = granularity !== 'daily' 
        ? aggregatedClientsData.map(item => ({
            date: item.date,
            result: item.totalUsers || 0
          }))
        : [];
      
      // Prompts Count chart
      const promptsCountChartData = aggregatedPromptsData.map(item => ({
        date: item.date,
        result: item.result
      }));
      
      // Prompts per User chart
      const promptsPerUserChartData = aggregatedPromptsData.map((promptItem) => {
        const correspondingDAU = aggregatedClientsData.find(dau => dau.date === promptItem.date);
        // For weekly/monthly: use total users (sum of daily users), not average DAU
        // For daily: use result (DAU) directly
        const userCount = granularity !== 'daily' && correspondingDAU?.totalUsers 
          ? correspondingDAU.totalUsers 
          : correspondingDAU?.result || 0;
        const promptsPerUser = userCount > 0 
          ? promptItem.result / userCount 
          : 0;
        return {
          date: promptItem.date,
          result: Math.round(promptsPerUser * 10) / 10
        };
      });
      
      // Input Tokens chart
      const inputTokensChartData = aggregatedPromptsData.map(item => ({
        date: item.date,
        result: item.input_tokens || 0
      }));
      
      // Output Tokens chart
      const outputTokensChartData = aggregatedPromptsData.map(item => ({
        date: item.date,
        result: item.output_tokens || 0
      }));
      
      // Calculate summary statistics from raw daily data (independent of granularity)
      const summaryStats = calculateSummaryStats(data.clients_by_day, data.prompts_count);
      
      // Combine all metrics into a single data structure (always include all, filtering happens via series)
      const combinedChartData = aggregatedClientsData.map((dauItem) => {
        const promptItem = aggregatedPromptsData.find(p => p.date === dauItem.date);
        const userCount = granularity !== 'daily' && dauItem.totalUsers 
          ? dauItem.totalUsers 
          : dauItem.result || 0;
        const promptsPerUser = userCount > 0 && promptItem 
          ? promptItem.result / userCount 
          : 0;
        
        const combined = {
          date: dauItem.date,
          dau: dauItem.result,
          totalUsers: granularity !== 'daily' ? dauItem.totalUsers : dauItem.result,
          promptsCount: promptItem?.result || 0,
          promptsPerUser: Math.round(promptsPerUser * 10) / 10,
          inputTokens: promptItem?.input_tokens || 0,
          outputTokens: promptItem?.output_tokens || 0,
        };
        
        return combined;
      });
      
      // Build series configuration for MultiSeriesChart based on current typeFilters
      const series = [];
      const colors = typeColors;
      
      if (typeFilters.dau) {
        series.push({ key: 'dau', name: 'DAU', color: colors.dau });
      }
      if (typeFilters.totalUsers) {
        series.push({ key: 'totalUsers', name: 'Total Users', color: colors.totalUsers });
      }
      if (typeFilters.promptsCount) {
        series.push({ key: 'promptsCount', name: 'Prompts Count', color: colors.promptsCount });
      }
      if (typeFilters.promptsPerUser) {
        series.push({ key: 'promptsPerUser', name: 'Prompts/user', color: colors.promptsPerUser });
      }
      if (typeFilters.inputTokens) {
        series.push({ key: 'inputTokens', name: 'Input Tokens', color: colors.inputTokens });
      }
      if (typeFilters.outputTokens) {
        series.push({ key: 'outputTokens', name: 'Output Tokens', color: colors.outputTokens });
      }
      
      setChartData({ 
        ...data,
        aggregated_clients_by_day: aggregatedClientsData,
        aggregated_prompts_count: aggregatedPromptsData,
        dauChartData,
        totalUsersChartData,
        promptsCountChartData,
        promptsPerUserChartData,
        inputTokensChartData,
        outputTokensChartData,
        summaryStats,
        combinedChartData,
        series
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  return <div>
    <div className='RagChatbotDetailPage__row'>
      <div className="RagChatbotDetailPage__datePicker">
        <div className="RagChatbotDetailPage__datePicker--label">Start Date</div>
        <DatePicker selected={startDate}
          onChange={(v) => {
            setStartDate(v);
          }}
          disabled={loading}
          maxDate={endDate} />
      </div>
      <div className="RagChatbotDetailPage__datePicker">
        <div className="RagChatbotDetailPage__datePicker--label">End Date</div>
        <DatePicker selected={endDate}
          onChange={(v) => {
            setEndDate(v)
          }}
          disabled={loading}
          minDate={startDate}
          maxDate={currentDate} />
      </div>
      <Button className={"RagChatbotDetailPage__datePicker--button"}
        color={"green"}
        loading={loading}
        title={"Update Chart"}
        onClick={fetchChartData} />
    </div>

    {/* Summary Stats Bar */}
    {chartData.summaryStats && (
      <div className='RagChatbotDetailPage__summary-stats'>
        <div className='RagChatbotDetailPage__summary-stat'>
          <div className='RagChatbotDetailPage__summary-stat--label'>Total Users</div>
          <div className='RagChatbotDetailPage__summary-stat--value'>
            {chartData.summaryStats.totalUsers.toLocaleString()}
          </div>
        </div>
        <div className='RagChatbotDetailPage__summary-stat'>
          <div className='RagChatbotDetailPage__summary-stat--label'>Avg DAU</div>
          <div className='RagChatbotDetailPage__summary-stat--value'>
            {chartData.summaryStats.avgDAU}
            {chartData.summaryStats.comparison && chartData.summaryStats.comparison.dauChange !== 0 && (
              <span className={classNames('RagChatbotDetailPage__summary-stat--change', {
                'positive': chartData.summaryStats.comparison.dauChange > 0,
                'negative': chartData.summaryStats.comparison.dauChange < 0
              })}>
                ({chartData.summaryStats.comparison.dauChange > 0 ? '+' : ''}{chartData.summaryStats.comparison.dauChange}% from last {chartData.summaryStats.comparison.period})
              </span>
            )}
          </div>
        </div>
        <div className='RagChatbotDetailPage__summary-stat'>
          <div className='RagChatbotDetailPage__summary-stat--label'>Peak DAU</div>
          <div className='RagChatbotDetailPage__summary-stat--value'>
            {chartData.summaryStats.peakDAU.value}
            {chartData.summaryStats.peakDAU.date && (
              <span className='RagChatbotDetailPage__summary-stat--date'>
                ({moment(chartData.summaryStats.peakDAU.date).format('MMM DD')})
              </span>
            )}
          </div>
        </div>
        <div className='RagChatbotDetailPage__summary-stat'>
          <div className='RagChatbotDetailPage__summary-stat--label'>Total Prompts</div>
          <div className='RagChatbotDetailPage__summary-stat--value'>
            {chartData.summaryStats.totalPrompts.toLocaleString()}
            {chartData.summaryStats.comparison && chartData.summaryStats.comparison.promptsChange !== 0 && (
              <span className={classNames('RagChatbotDetailPage__summary-stat--change', {
                'positive': chartData.summaryStats.comparison.promptsChange > 0,
                'negative': chartData.summaryStats.comparison.promptsChange < 0
              })}>
                ({chartData.summaryStats.comparison.promptsChange > 0 ? '+' : ''}{chartData.summaryStats.comparison.promptsChange}% from last {chartData.summaryStats.comparison.period})
              </span>
            )}
          </div>
        </div>
        <div className='RagChatbotDetailPage__summary-stat'>
          <div className='RagChatbotDetailPage__summary-stat--label'>Avg Prompts/User</div>
          <div className='RagChatbotDetailPage__summary-stat--value'>{chartData.summaryStats.avgPromptsPerUser}</div>
        </div>
      </div>
    )}

    {/* Granularity Selector */}
    <div className='RagChatbotDetailPage__granularity-selector'>
      <div className='RagChatbotDetailPage__granularity-selector--buttons'>
        <button 
          className={classNames('RagChatbotDetailPage__granularity-button', { 'active': granularity === 'daily' })}
          onClick={() => setGranularity('daily')}
          disabled={loading}
        >
          Daily
        </button>
        <button 
          className={classNames('RagChatbotDetailPage__granularity-button', { 'active': granularity === 'weekly' })}
          onClick={() => setGranularity('weekly')}
          disabled={loading}
        >
          Weekly
        </button>
        <button 
          className={classNames('RagChatbotDetailPage__granularity-button', { 'active': granularity === 'monthly' })}
          onClick={() => setGranularity('monthly')}
          disabled={loading}
        >
          Monthly
        </button>
      </div>
    </div>


    {loading ? <Loader color='green' size='large' /> :
      chartData ?
        <div className='RagChatbotDetailPage__clients_chart'>
          <div className='RagChatbotDetailPage__chart-wrapper'>
            {memoizedChart}
          </div>
          <div className='RagChatbotDetailPage__source-filters'>
            <div className='RagChatbotDetailPage__filters-section'>
              <div className='RagChatbotDetailPage__filters-section--title'>Type</div>
              <div className="RagChatbotDetailPage__source-filters--options">
                <label 
                  className="RagChatbotDetailPage__source-filter" 
                  style={{ '--type-color': typeColors.dau }}
                >
                  <input
                    type="checkbox"
                    checked={typeFilters.dau}
                    onChange={() => handleTypeFilterChange('dau')}
                    disabled={loading}
                  />
                  <span>DAU</span>
                </label>
                {granularity !== 'daily' && (
                  <label 
                    className="RagChatbotDetailPage__source-filter" 
                    style={{ '--type-color': typeColors.totalUsers }}
                  >
                    <input
                      type="checkbox"
                      checked={typeFilters.totalUsers}
                      onChange={() => handleTypeFilterChange('totalUsers')}
                      disabled={loading}
                    />
                    <span>Total Users</span>
                  </label>
                )}
                <label 
                  className="RagChatbotDetailPage__source-filter" 
                  style={{ '--type-color': typeColors.promptsCount }}
                >
                  <input
                    type="checkbox"
                    checked={typeFilters.promptsCount}
                    onChange={() => handleTypeFilterChange('promptsCount')}
                    disabled={loading}
                  />
                  <span>Prompts Count</span>
                </label>
                <label 
                  className="RagChatbotDetailPage__source-filter" 
                  style={{ '--type-color': typeColors.promptsPerUser }}
                >
                  <input
                    type="checkbox"
                    checked={typeFilters.promptsPerUser}
                    onChange={() => handleTypeFilterChange('promptsPerUser')}
                    disabled={loading}
                  />
                  <span>Prompts/User</span>
                </label>
                <label 
                  className="RagChatbotDetailPage__source-filter" 
                  style={{ '--type-color': typeColors.inputTokens }}
                >
                  <input
                    type="checkbox"
                    checked={typeFilters.inputTokens}
                    onChange={() => handleTypeFilterChange('inputTokens')}
                    disabled={loading}
                  />
                  <span>Input Tokens</span>
                </label>
                <label 
                  className="RagChatbotDetailPage__source-filter" 
                  style={{ '--type-color': typeColors.outputTokens }}
                >
                  <input
                    type="checkbox"
                    checked={typeFilters.outputTokens}
                    onChange={() => handleTypeFilterChange('outputTokens')}
                    disabled={loading}
                  />
                  <span>Output Tokens</span>
                </label>
              </div>
            </div>
            <div className='RagChatbotDetailPage__filters-section'>
              <div className='RagChatbotDetailPage__filters-section--title'>Platforms</div>
              <div className="RagChatbotDetailPage__source-filters--options">
                <label className="RagChatbotDetailPage__source-filter">
                  <input
                    type="checkbox"
                    checked={sourceFilters.web}
                    onChange={() => handleSourceFilterChange('web')}
                    disabled={loading}
                  />
                  <span>Web</span>
                </label>
                <label className="RagChatbotDetailPage__source-filter">
                  <input
                    type="checkbox"
                    checked={sourceFilters.discord}
                    onChange={() => handleSourceFilterChange('discord')}
                    disabled={loading}
                  />
                  <span>Discord</span>
                </label>
              </div>
            </div>
          </div>
        </div> : <div className='EmptyState'>
          <div className={'EmptyState__title'}>No data available in the selected date range. Please adjust your date range.</div>
        </div>}
  </div>
}

const PromptsTab = ({ projectId, id }) => {
  const dispatch = useDispatch();
  const isMobile = useSelector(state => selectMobileLayout(state));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [prompts, setPrompts] = useState();
  const [curPrompts, setCurPrompts] = useState();
  const [curPage, setCurPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const currentDate = new Date();
  const iniStartDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, currentDate.getDate());
  const [startDate, setStartDate] = useState(iniStartDate);
  const [endDate, setEndDate] = useState(currentDate);
  const [isChanged, setIsChanged] = useState(false);
  const MAX_RECORDS_PER_PAGE = 30;

  useEffect(() => {
    if (!prompts) {
      fetchPrompts();
    }
  }, [id, prompts])

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      let sDate = moment(startDate).format('YYYY-MM-DD')
      let eDate = moment(endDate).format('YYYY-MM-DD')
      let res = await dispatch(Ai.actions.fetchChatbotPrompts(id, projectId, { start_timestamp: sDate, end_timestamp: eDate }));
      setPrompts(res);
      let cp = res.slice(0, MAX_RECORDS_PER_PAGE)
      setCurPrompts(cp);
      setTotalPages(Math.ceil(res.length / MAX_RECORDS_PER_PAGE));
      setCurPage(1);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      if (isChanged) setIsChanged(false);
    }
  }

  const handleDownload = async () => {
    function convertToCSV(objArray) {
      var array = typeof objArray != 'object' ? JSON.parse(objArray) : objArray;
      var str = '';

      var headers = Object.keys(array[0]);
      str += headers.map(header => `"${header}"`).join(',') + '\r\n';

      for (var i = 0; i < array.length; i++) {
        var row = headers
          .map(header => {
            let value = array[i][header];
            if (typeof value === 'string') {
              value = value.replace(/"/g, '""');
              return `"${value}"`;
            }
            return value;
          })
          .join(',');

        str += row + '\r\n';
      }
      return str;
    }

    var csv = convertToCSV(prompts.map(p => {
      // Format timestamp for CSV
      let formattedTimestamp = '';
      if (p.timestamp) {
        try {
          formattedTimestamp = moment(p.timestamp).format('YYYY-MM-DD HH:mm:ss');
        } catch (e) {
          formattedTimestamp = p.timestamp;
        }
      }
      
      return {
        prompts: p.question, 
        completion: p.answer,
        input_tokens: p.input_tokens || 0,
        output_tokens: p.output_tokens || 0,
        timestamp: formattedTimestamp
      };
    }));

    var blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', `${id}_prompts.csv`);
    a.click();
    window.URL.revokeObjectURL(url);
  }

  const handlePageChange = (page) => {
    setCurPage(page);
    setCurPrompts(prompts.slice(MAX_RECORDS_PER_PAGE * (page - 1), MAX_RECORDS_PER_PAGE * page));
  }

  return <>
    <div className='RagChatbotDetailPage__row'>
      <div className="RagChatbotDetailPage__datePicker">
        <div className="RagChatbotDetailPage__datePicker--label">Start Date</div>
        <DatePicker selected={startDate}
          onChange={(v) => {
            setStartDate(v);
            if (!isChanged) setIsChanged(true)
          }}
          disabled={loading}
          maxDate={endDate} />
      </div>
      <div className="RagChatbotDetailPage__datePicker">
        <div className="RagChatbotDetailPage__datePicker--label">End Date</div>
        <DatePicker selected={endDate}
          onChange={(v) => {
            setEndDate(v)
            if (!isChanged) setIsChanged(true)
          }}
          disabled={loading}
          minDate={startDate}
          maxDate={currentDate} />
      </div>
      <Button className={"RagChatbotDetailPage__datePicker--button"}
        color={"green"}
        loading={loading}
        title={isChanged ? "Update Data" : "Download All Data"}
        onClick={isChanged ? fetchPrompts : handleDownload}
        size={isMobile ? 'large' : 'medium'} />
    </div>
    {prompts && prompts.length > 0 && <div className='RagChatbotDetailPage__prompt-table'>
      <div className='RagChatbotDetailPage__prompt-table--header-row'>
        <div className='RagChatbotDetailPage__prompt-table--cell timestamp'>Date</div>
        <div className='RagChatbotDetailPage__prompt-table--cell question'>Prompt</div>
        <div className='RagChatbotDetailPage__prompt-table--cell answer'>Completion</div>
        <div className='RagChatbotDetailPage__prompt-table--cell expand-button-header'></div>
      </div>
      {curPrompts && curPrompts.map((prompt, i) => {
        return <PromptTableRow prompt={prompt} i={i} key={i} />
      })}
      {/* {curPrompts && curPrompts.map((prompt, i) => {
          return <div className='RagChatbotDetailPage__prompt-table--row' key={i}>
            <div className='RagChatbotDetailPage__prompt-table--cell ip' title={prompt.client_ip}>
              {prompt.client_ip}
            </div>
            <div className='RagChatbotDetailPage__prompt-table--cell question' title={prompt.question}>
              {prompt.question}
            </div>
            <div className='RagChatbotDetailPage__prompt-table--cell answer' title={prompt.answer}>
              {prompt.answer}
            </div>
          </div>
        })} */}
      <Pagination
        size={'lg'}
        currentPage={curPage}
        totalPages={totalPages}
        onPageChange={handlePageChange} />
    </div>}
    {prompts && prompts.length === 0 && <div className='EmptyState'>
      <div className={'EmptyState__title'}>No data available in the selected date range. Please adjust your date range.</div>
    </div>}
  </>
}

const PromptTableRow = ({ prompt, i }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const questionRef = useRef(null);
  const answerRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'not available';
    try {
      return moment(timestamp).format('MMM DD, YYYY HH:mm:ss');
    } catch (e) {
      return 'not available';
    }
  };

  // Check if content is truncated
  useEffect(() => {
    const isQuestionTruncated =
      questionRef.current && questionRef.current.scrollWidth > questionRef.current.clientWidth;
    const isAnswerTruncated =
      answerRef.current && answerRef.current.scrollWidth > answerRef.current.clientWidth;

    setIsTruncated(isQuestionTruncated || isAnswerTruncated);
  }, [prompt.question, prompt.answer, prompt.client_ip]);

  return (
    <div
      className={`RagChatbotDetailPage__prompt-table--row ${isExpanded ? 'expanded' : ''}`}
      key={i}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <div 
        className={classNames('RagChatbotDetailPage__prompt-table--cell timestamp', { 'expand': isExpanded })}
        title={prompt.timestamp || 'not available'}>
        {formatTimestamp(prompt.timestamp)}
      </div>
      <div
        className={classNames('RagChatbotDetailPage__prompt-table--cell question', { 'expand': isExpanded })}
        title={prompt.question}
        ref={questionRef}>
        {prompt.question}
      </div>
      <div
        className={classNames('RagChatbotDetailPage__prompt-table--cell answer', { 'expand': isExpanded })}
        title={prompt.answer}
        ref={answerRef}>
        {prompt.answer}
      </div>
      <div 
        className={`expand-button ${isExpanded ? 'arrow-up' : 'arrow-down'}`}
        onClick={(e) => {
          e.stopPropagation();
        }}
        style={{ marginLeft: 'auto' }}
      ></div>
      {isExpanded && (
        <div className='RagChatbotDetailPage__prompt-table--details-row'>
          <div className='RagChatbotDetailPage__prompt-table--details-cell'>
            <span className='RagChatbotDetailPage__prompt-table--details-label'>Input Tokens:</span>
            <span className='RagChatbotDetailPage__prompt-table--details-value'>{prompt.input_tokens || 0}</span>
          </div>
          <div className='RagChatbotDetailPage__prompt-table--details-cell'>
            <span className='RagChatbotDetailPage__prompt-table--details-label'>Output Tokens:</span>
            <span className='RagChatbotDetailPage__prompt-table--details-value'>{prompt.output_tokens || 0}</span>
          </div>
        </div>
      )}
    </div>
  );
};

